import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import PageTransition from '@/components/PageTransition';
import logo from '/media/Blue_logo_Round.png';
import { useAuth } from '@/lib/AuthContext';
import { getAuthErrorMessage } from '@/lib/authMessages';
import { supabase } from '@/lib/supabase';
import { createPageUrl } from './Utils';
import { TutorialProvider, useTutorial } from '@/lib/TutorialContext';
import { useTheme } from '@/lib/ThemeContext';
import TutorialOverlay from '@/components/TutorialOverlay';
import {
  Home, Bell, Settings, LogOut, Menu, X, Users, Building2,
  GraduationCap, Baby, Calendar, Utensils, Camera, MessageSquare,
  Shield, ChevronRight, ChevronDown, Loader2, UserCog, Lock, Eye, EyeOff, CheckCircle2, XCircle, Bug, Megaphone, CreditCard, Wallet, Clock
} from 'lucide-react';
import { LanguageProvider, useLanguage } from '@/components/LanguageContext';
import RequiredConsentsDialog from '@/components/RequiredConsentsDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { validatePassword, getPasswordStrength } from '@/lib/passwordValidation';
import { logger } from '@/lib/logger';
import BottomNav from '@/components/BottomNav';
import TeacherBottomNav from '@/components/TeacherBottomNav';
import BranchAdminBottomNav from '@/components/BranchAdminBottomNav';
import SuperAdminBottomNav from '@/components/SuperAdminBottomNav';
import SystemAnnouncementBanner from '@/components/SystemAnnouncementBanner';
import PasswordExpiryModal from '@/components/auth/PasswordExpiryModal';
import MFAEnrollment from '@/components/auth/MFAEnrollment';
import MFAChallenge from '@/components/auth/MFAChallenge';
import BiometricSetup, { isBiometricAvailable, shouldPromptBiometricSetup } from '@/components/auth/BiometricSetup';
import SecureAuthChoice from '@/components/auth/SecureAuthChoice';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useInactivityTimeout } from '@/hooks/useInactivityTimeout';
import { useTabManager } from '@/hooks/useTabManager';
import { useSessionLifecycle } from '@/hooks/useSessionLifecycle';
import { useUnreadNotificationCount } from '@/hooks/useNotifications';
import { useEnabledModulesForUser } from '@/hooks/useBranchModules';
import { useHasMultipleContexts } from '@/hooks/useAuth';
import { RoleSwitcher, RoleSwitcherMobile, RoleSwitcherMobileButton } from '@/components/RoleSwitcher';

// Role-based color schemes
const ROLE_COLORS = {
  parent: {
    gradient: 'from-blue-600 to-indigo-600',
    headerBg: 'bg-gradient-to-r from-blue-600 to-indigo-600',
    sidebarBg: 'bg-gradient-to-b from-blue-600 via-blue-700 to-indigo-800',
    navItem: 'text-white/70 hover:text-white hover:bg-white/10',
    navActive: 'bg-white/20 text-white font-semibold border-l-4 border-white shadow-lg shadow-black/10',
    navActiveIcon: 'text-white',
    infoCardBg: 'bg-white/15 border-white/20',
    infoCardText: 'text-white/90',
    infoCardLabel: 'text-white/70',
    infoCardIcon: 'text-white/80',
  },
  teacher: {
    gradient: 'from-emerald-600 to-teal-600',
    headerBg: 'bg-gradient-to-r from-emerald-600 to-teal-600',
    sidebarBg: 'bg-gradient-to-b from-emerald-600 via-emerald-700 to-teal-800',
    navItem: 'text-white/70 hover:text-white hover:bg-white/10',
    navActive: 'bg-white/20 text-white font-semibold border-l-4 border-white shadow-lg shadow-black/10',
    navActiveIcon: 'text-white',
    infoCardBg: 'bg-white/15 border-white/20',
    infoCardText: 'text-white/90',
    infoCardLabel: 'text-white/70',
    infoCardIcon: 'text-white/80',
  },
  branch_admin: {
    gradient: 'from-indigo-600 to-violet-600',
    headerBg: 'bg-gradient-to-r from-indigo-600 to-violet-600',
    sidebarBg: 'bg-gradient-to-b from-indigo-600 via-indigo-700 to-violet-800',
    navItem: 'text-white/70 hover:text-white hover:bg-white/10',
    navActive: 'bg-white/20 text-white font-semibold border-l-4 border-white shadow-lg shadow-black/10',
    navActiveIcon: 'text-white',
    infoCardBg: 'bg-white/15 border-white/20',
    infoCardText: 'text-white/90',
    infoCardLabel: 'text-white/70',
    infoCardIcon: 'text-white/80',
  },
  super_admin: {
    gradient: 'from-crimson-600 to-crimson-800',
    headerBg: 'bg-gradient-to-r from-crimson-600 to-crimson-800',
    sidebarBg: 'bg-gradient-to-b from-crimson-600 via-crimson-700 to-crimson-800',
    navItem: 'text-white/70 hover:text-white hover:bg-white/10',
    navActive: 'bg-white/20 text-white font-semibold border-l-4 border-white shadow-lg shadow-black/10',
    navActiveIcon: 'text-white',
    infoCardBg: 'bg-white/15 border-white/20',
    infoCardText: 'text-white/90',
    infoCardLabel: 'text-white/70',
    infoCardIcon: 'text-white/80',
  },
};

// Role translations
const ROLE_TRANSLATIONS = {
  'super_admin': 'SuperAdmin',
  'branch_admin': 'Administrator Placówki',
  'teacher': 'Nauczyciel',
  'parent': 'Rodzic'
};

function LayoutContent({ children, currentPageName }) {
  const { user: authUser, userProfile, isAuthenticated, isLoading: authLoading, signOut, passwordRecoveryMode, clearPasswordRecoveryMode, updatePassword, authError, retryAuth, isReconnecting, passwordExpiryStatus, mfaStatus, refreshMfaStatus } = useAuth();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, language } = useLanguage();
  const { startTutorial, shouldAutoStart } = useTutorial();

  // Password recovery dialog state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Password expiry modal state - shows at login when password is expiring or expired
  const [passwordExpiryModalDismissed, setPasswordExpiryModalDismissed] = useState(false);

  // Biometric setup prompt state - shows on mobile/PWA after login
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [biometricCheckDone, setBiometricCheckDone] = useState(false);
  
  // Secure auth choice and enrollment state
  const [showSecureAuthChoice, setShowSecureAuthChoice] = useState(false);
  const [showMFAEnrollment, setShowMFAEnrollment] = useState(false);
  const [showBiometricEnrollment, setShowBiometricEnrollment] = useState(false);
  const [biometricAvailableForEnrollment, setBiometricAvailableForEnrollment] = useState(false);

  // Bug report - navigate to BugReports page with dialog open
  const handleReportBug = () => {
    navigate('/BugReports', { state: { openCreateDialog: true } });
  };

  // Collapsible sidebar sections state - collapsed by default
  const [strukturaExpanded, setStrukturaExpanded] = useState(false);

  // Mobile detection for parent UI
  const isMobile = useIsMobile();

  // Inactivity timeout - logs out after 30 minutes of no activity (with 5 min warning)
  useInactivityTimeout(30, 5);

  // Tab manager - enforces single-tab-per-device policy
  // When a new tab opens, existing tabs are signed out
  useTabManager({ isAuthenticated });

  // Session lifecycle - handles tab close vs refresh detection
  // Tab close terminates session, page refresh preserves it
  useSessionLifecycle({ isAuthenticated });

  // Use userProfile from AuthContext as our user
  const user = userProfile;

  // Get unread notification count - uses React Query for automatic updates
  const { data: rawUnreadCount = 0 } = useUnreadNotificationCount(user?.id);
  // Respect master notification toggle - show 0 if notifications are disabled
  const notificationsEnabled = user?.preferences?.notifications_enabled ?? true;
  const unreadCount = notificationsEnabled ? rawUnreadCount : 0;

  // Check if user is a parent on mobile - use bottom nav instead of sidebar
  const isParentOnMobile = isMobile && user && (user.active_role === 'parent' || user.roles?.[0] === 'parent');

  // Check if user is a teacher on mobile - use bottom nav instead of sidebar
  const isTeacherOnMobile = isMobile && user && (user.active_role === 'teacher' || user.roles?.[0] === 'teacher');

  // Check if user is a branch_admin on mobile - use bottom nav instead of sidebar
  const isBranchAdminOnMobile = isMobile && user && (user.active_role === 'branch_admin' || user.roles?.[0] === 'branch_admin');

  // Check if user is a super_admin on mobile - use bottom nav instead of sidebar
  const isSuperAdminOnMobile = isMobile && user && (user.active_role === 'super_admin' || user.roles?.[0] === 'super_admin');

  // Combined check for any role using bottom nav
  const useBottomNav = isParentOnMobile || isTeacherOnMobile || isBranchAdminOnMobile || isSuperAdminOnMobile;

  // Get current role for color scheme
  const currentRole = user?.active_role || user?.roles?.[0] || 'parent';

  // Use theme context for colors (user preference or role default)
  const { getActiveColors } = useTheme();
  const roleColors = getActiveColors();

  useEffect(() => {
    // Auth is handled by AuthContext, just set loading based on auth state
    setLoading(authLoading);

    // Redirect to login if not authenticated and not loading
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Check if user needs onboarding (first-time user)
  useEffect(() => {
    if (!authLoading && userProfile && isAuthenticated) {
      // Skip onboarding check for super_admin
      const role = userProfile.active_role || userProfile.primary_role;
      if (role === 'super_admin') return;

      // Check if onboarding is completed
      const onboardingCompleted = userProfile.metadata?.onboarding_completed;
      if (!onboardingCompleted && location.pathname !== '/welcome') {
        navigate('/welcome');
      }
    }
  }, [authLoading, userProfile, isAuthenticated, location.pathname, navigate]);

  // Tutorial overlay disabled until fully implemented (nav targets vs branch admin UI)
  // Auto-start tutorial when redirected from welcome page
  // useEffect(() => {
  //   if (searchParams.get('tutorial') === 'true' && shouldAutoStart) {
  //     const timer = setTimeout(() => {
  //       startTutorial();
  //       window.history.replaceState({}, '', location.pathname);
  //     }, 500);
  //     return () => clearTimeout(timer);
  //   }
  // }, [searchParams, shouldAutoStart, startTutorial, location.pathname]);

  useEffect(() => {
    if (user) {
      // Check active_role or first role in roles array
      const currentRole = user.active_role || user.roles?.[0];
      if (currentRole === 'branch_admin' && user.branch_ids?.length > 0) {
        loadBranches();
      }
    }
  }, [user]);

  // Check if we should show secure auth choice dialog (for branch_admins)
  useEffect(() => {
    const checkSecureAuthChoice = async () => {
      if (!isAuthenticated || !userProfile || !mfaStatus?.needsEnrollment) return;
      
      // Only show choice dialog for branch_admins with allowSkip
      if (!mfaStatus?.allowSkip) return;
      
      // Check biometric availability
      const available = await isBiometricAvailable();
      setBiometricAvailableForEnrollment(available);
      setShowSecureAuthChoice(true);
    };

    checkSecureAuthChoice();
  }, [isAuthenticated, userProfile, mfaStatus?.needsEnrollment, mfaStatus?.allowSkip]);

  // Check if we should prompt for biometric setup (on mobile/PWA)
  useEffect(() => {
    const checkBiometricSetup = async () => {
      if (!isAuthenticated || !user || biometricCheckDone) return;

      // Don't show if user has other modals showing
      if (passwordRecoveryMode || passwordExpiryStatus?.isExpired || mfaStatus?.needsEnrollment || mfaStatus?.needsChallenge || showSecureAuthChoice) {
        return;
      }

      // Check if we should prompt (mobile/PWA and hasn't skipped recently)
      if (!shouldPromptBiometricSetup()) {
        setBiometricCheckDone(true);
        return;
      }

      // Check if biometric is available on this device
      const available = await isBiometricAvailable();
      if (!available) {
        setBiometricCheckDone(true);
        return;
      }

      // Check if user already has biometric credentials
      try {
        const { data: existingCredentials } = await supabase
          .from('webauthn_credentials')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);

        if (existingCredentials && existingCredentials.length > 0) {
          // User already has biometric set up
          setBiometricCheckDone(true);
          return;
        }

        // Show the biometric setup prompt
        setShowBiometricSetup(true);
        setBiometricCheckDone(true);
      } catch (error) {
        logger.error('Error checking biometric credentials:', error);
        setBiometricCheckDone(true);
      }
    };

    checkBiometricSetup();
  }, [isAuthenticated, user, biometricCheckDone, passwordRecoveryMode, passwordExpiryStatus, mfaStatus]);

  const [branches, setBranches] = useState([]);
  const [currentBranch, setCurrentBranch] = useState(null);

  const loadBranches = async () => {
    try {
      const { data: allBranches, error } = await supabase
        .from('branches')
        .select('*')
        .in('id', user.branch_ids || []);
      
      if (error) throw error;

      const branchList = allBranches || [];
      setBranches(branchList);
      
      if (branchList.length > 0) {
        // Use stored current_branch_id or default to first branch
        const currentId = user.current_branch_id || user.branch_ids[0];
        const current = branchList.find(b => b.id === currentId) || branchList[0];
        setCurrentBranch(current);
      }
    } catch (e) {
      logger.error('Failed to load branches:', e);
    }
  };

  const switchBranch = async (branch) => {
    setCurrentBranch(branch);
    try {
      const { error } = await supabase
        .from('app_users')
        .update({ current_branch_id: branch.id })
        .eq('id', user.id);
      
      if (error) throw error;
      window.location.reload();
    } catch (e) {
      logger.error('Failed to switch branch:', e);
      window.location.reload();
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(queryClient);
      window.location.href = '/login';
    } catch (e) {
      logger.error('Logout failed:', e);
    }
  };

  // Password recovery handlers
  const passwordValidation = validatePassword(newPassword);
  const passwordStrength = getPasswordStrength(newPassword);
  const passwordsMatch = newPassword === confirmPassword;
  const canSavePassword = passwordValidation.isValid && passwordsMatch && newPassword.length > 0;

  const handleRecoveryPasswordChange = async () => {
    if (!canSavePassword) return;

    setChangingPassword(true);
    try {
      await updatePassword(newPassword);
      toast.success('Hasło zostało zmienione pomyślnie');
      // Clear form and close dialog
      setNewPassword('');
      setConfirmPassword('');
      setPasswordTouched(false);
    } catch (error) {
      logger.error('Password change error:', error);
      toast.error(error.message || 'Nie udało się zmienić hasła');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleCloseRecoveryDialog = () => {
    clearPasswordRecoveryMode();
    setNewPassword('');
    setConfirmPassword('');
    setPasswordTouched(false);
  };

  const getNavItems = (isModuleEnabled = () => true) => {
    if (!user) return [];
    
    // Get active role or first role from roles array
    const role = user.active_role || user.roles?.[0] || 'parent';
    
    const baseItems = [
      { name: t('home'), icon: Home, page: 'Home' }
    ];

    if (role === 'super_admin') {
          return [
            { name: 'Start', icon: Home, page: 'PlatformDashboard' },
            { name: 'Placówki', icon: Building2, page: 'Tenants' },
            { name: 'Administratorzy', icon: Users, page: 'Administrators' },
            { name: 'Zgłoszenia', icon: Bug, page: 'BugReports' },
            { name: 'Ogłoszenia systemowe', icon: Megaphone, page: 'SystemAnnouncements' },
            { name: 'Osierocone rekordy', icon: Baby, page: 'OrphanedRecords' }
          ];
        }

    if (role === 'branch_admin') {
      const strukturaChildren = [
        { name: t('groups'), icon: Users, page: 'Groups' },
        { name: t('children'), icon: Baby, page: 'Children' },
        { name: t('teachers'), icon: GraduationCap, page: 'Teachers' },
        { name: t('parents'), icon: Users, page: 'Parents' },
      ];
      const items = [
        ...baseItems,
        { name: 'Struktura', icon: Building2, isGroup: true, children: strukturaChildren },
        ...(isModuleEnabled('grafik') ? [{ name: 'Grafik', icon: Calendar, page: 'StaffSchedule' }] : []),
        ...(isModuleEnabled('jadlospis') ? [{ name: t('menu'), icon: Utensils, page: 'MenuManagement' }] : []),
        { name: 'Rozliczenia', icon: CreditCard, page: 'Billing' },
        { name: t('branchSettings'), icon: Settings, page: 'BranchSettings' }
      ];
      return items;
    }

    if (role === 'teacher') {
      return [
        { name: t('home'), icon: Home, page: 'TeacherDashboard' },
        { name: t('dailyUpdates'), icon: Calendar, page: 'DailyUpdates' },
        ...(isModuleEnabled('grafik') ? [{ name: 'Mój grafik', icon: Clock, page: 'MySchedule' }] : []),
        ...(isModuleEnabled('media') ? [{ name: t('photos'), icon: Camera, page: 'PhotosManagement' }] : []),
        { name: t('messages'), icon: MessageSquare, page: 'Announcements' },
        { name: 'Szablony', icon: Calendar, page: 'Templates' },
        ...(isModuleEnabled('jadlospis') ? [{ name: t('menu'), icon: Utensils, page: 'TeacherMenu' }] : []),
        { name: t('settings'), icon: Settings, page: 'Settings' }
      ];
    }

    // Parent role
    return [
      { name: t('home'), icon: Home, page: 'Home' },
      { name: t('messages'), icon: MessageSquare, page: 'Messages' },
      ...(isModuleEnabled('media') ? [{ name: t('photos'), icon: Camera, page: 'Photos' }] : []),
      ...(isModuleEnabled('jadlospis') ? [{ name: t('menu'), icon: Utensils, page: 'WeeklyMenu' }] : []),
      ...(isModuleEnabled('rozliczenia') ? [{ name: 'Rozliczenia', icon: Wallet, page: 'ParentBilling' }] : []),
      { name: t('settings'), icon: Settings, page: 'Settings' }
    ];
  };

  const { isModuleEnabled } = useEnabledModulesForUser();
  const hasMultipleContexts = useHasMultipleContexts();
  const navItems = getNavItems(isModuleEnabled);

  // Public pages that don't need layout
  const publicPages = ['AcceptInvite'];
  if (publicPages.includes(currentPageName)) {
    return <>{children}</>;
  }

  // Show loading spinner while auth is being checked or reconnecting
  if (loading || authLoading || isReconnecting) {
    // #region agent log
    // Ingest disabled: 127.0.0.1 is blocked by CSP in prod and can cause reload
    // #endregion
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        {isReconnecting && (
          <p className="text-sm text-slate-600">Odzyskiwanie sesji...</p>
        )}
      </div>
    );
  }

  // If not authenticated after loading completes, show error or redirect
  if (!isAuthenticated || !user) {
    // If there's an auth error (like timeout), show error with retry
    if (authError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Problem z połączeniem</h2>
            <p className="text-slate-600 mb-6">
              {getAuthErrorMessage(authError)}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => retryAuth?.()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Spróbuj ponownie
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  // Clear any stale data and redirect to login
                  localStorage.clear();
                  window.location.href = '/login';
                }}
              >
                Zaloguj się
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // No error - just show loading while redirect happens
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const currentRoleForDisplay = user.active_role || user.roles?.[0];
  let branchNameToDisplay = null;
  if (currentRoleForDisplay === 'branch_admin' && currentBranch) {
    branchNameToDisplay = currentBranch.display_name || currentBranch.name;
  } else if (currentRoleForDisplay === 'teacher' && user.branch_name) {
    branchNameToDisplay = user.branch_name;
  } else if (currentRoleForDisplay === 'parent' && user.child_branches?.length > 0) {
    branchNameToDisplay = user.child_branches.join(', ');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Required Consents Dialog for Staff */}
      <RequiredConsentsDialog />

      {/* System Announcements Banner */}
      <SystemAnnouncementBanner />

      {/* Password Expiry Modal - Shows at login when password is expiring (3 days) or expired (90+ days) */}
      <PasswordExpiryModal
        isOpen={
          passwordExpiryStatus &&
          (passwordExpiryStatus.isExpired || passwordExpiryStatus.isWarning) &&
          !passwordExpiryModalDismissed &&
          location.pathname !== '/set-password' &&
          location.pathname !== '/forgot-password'
        }
        onClose={() => setPasswordExpiryModalDismissed(true)}
        isExpired={passwordExpiryStatus?.isExpired || false}
        daysRemaining={passwordExpiryStatus?.daysRemaining || 0}
      />

      {/* Secure Auth Choice Dialog - Shows for branch_admins to choose between MFA and Biometrics */}
      <SecureAuthChoice
        isOpen={showSecureAuthChoice}
        biometricAvailable={biometricAvailableForEnrollment}
        onChooseMFA={async () => {
          setShowSecureAuthChoice(false);
          setShowMFAEnrollment(true);
        }}
        onChooseBiometrics={async () => {
          setShowSecureAuthChoice(false);
          setShowBiometricEnrollment(true);
        }}
        onLogout={async () => {
          await signOut();
        }}
      />

      {/* MFA Enrollment Modal - Shows for super_admin or when branch_admin chooses MFA */}
      <MFAEnrollment
        isOpen={showMFAEnrollment || (mfaStatus?.needsEnrollment && !mfaStatus?.allowSkip) || false}
        allowSkip={false}
        showSkipButton={showMFAEnrollment && mfaStatus?.allowSkip}
        onEnrolled={async () => {
          setShowMFAEnrollment(false);
          await refreshMfaStatus();
        }}
        onCancelled={async () => {
          // If this was opened from choice dialog (branch_admin), go back to choice
          if (showMFAEnrollment && mfaStatus?.allowSkip) {
            setShowMFAEnrollment(false);
            // Small delay to ensure state is reset before showing choice dialog
            setTimeout(() => {
              setShowSecureAuthChoice(true);
            }, 100);
          } else {
            // Super admin cannot skip - sign out
            await signOut();
          }
        }}
      />

      {/* Biometric Enrollment Modal - Shows when branch_admin chooses biometrics */}
      <BiometricSetup
        isOpen={showBiometricEnrollment}
        isRequired={true}
        onComplete={async () => {
          setShowBiometricEnrollment(false);
          await refreshMfaStatus();
        }}
        onSkip={async () => {
          // If they skip, reset and show the choice dialog again
          setShowBiometricEnrollment(false);
          // Small delay to ensure state is reset before showing choice dialog
          setTimeout(() => {
            setShowSecureAuthChoice(true);
          }, 100);
        }}
      />

      {/* MFA Challenge Modal - Shows for admin users (super_admin/branch_admin) with MFA at aal1 level */}
      <MFAChallenge
        isOpen={(mfaStatus?.needsChallenge || false) && (userProfile?.preferences?.secure_auth?.mfa_enabled !== false)}
        onVerified={async () => {
          await refreshMfaStatus();
        }}
        onSignOut={async () => {
          await signOut();
        }}
      />

      {/* Biometric Setup Modal - Shows for mobile/PWA users after login (optional, not required) */}
      <BiometricSetup
        isOpen={showBiometricSetup && !showSecureAuthChoice && !showBiometricEnrollment}
        onComplete={() => {
          setShowBiometricSetup(false);
        }}
        onSkip={() => {
          setShowBiometricSetup(false);
        }}
      />

      {/* Password Recovery Dialog - Don't show if already on /set-password page */}
      <Dialog open={passwordRecoveryMode && location.pathname !== '/set-password'} onOpenChange={(open) => !open && handleCloseRecoveryDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Lock className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <DialogTitle className="text-center">Ustaw nowe hasło</DialogTitle>
            <DialogDescription className="text-center">
              Wprowadź nowe hasło do swojego konta. Upewnij się, że jest silne i bezpieczne.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="recovery-new-password">Nowe hasło</Label>
              <div className="relative">
                <Input
                  id="recovery-new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setPasswordTouched(true);
                  }}
                  placeholder="Wprowadź nowe hasło"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>

              {/* Password Strength Gauge */}
              {passwordTouched && newPassword.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">Siła hasła:</span>
                    <span className={cn(
                      "font-medium",
                      passwordStrength.score < 2 ? "text-red-500" :
                      passwordStrength.score < 3 ? "text-yellow-500" :
                      passwordStrength.score < 4 ? "text-blue-500" : "text-green-500"
                    )}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <Progress
                    value={(passwordStrength.score / 4) * 100}
                    className={cn(
                      "h-2",
                      passwordStrength.score < 2 ? "[&>div]:bg-red-500" :
                      passwordStrength.score < 3 ? "[&>div]:bg-yellow-500" :
                      passwordStrength.score < 4 ? "[&>div]:bg-blue-500" : "[&>div]:bg-green-500"
                    )}
                  />

                  {/* Requirements list */}
                  <div className="grid grid-cols-2 gap-1 text-xs mt-2">
                    {passwordValidation.requirements.map((req, i) => (
                      <div key={i} className={cn(
                        "flex items-center gap-1",
                        req.met ? "text-green-600" : "text-slate-400"
                      )}>
                        {req.met ? (
                          <CheckCircle2 className="w-3 h-3" />
                        ) : (
                          <XCircle className="w-3 h-3" />
                        )}
                        <span>{req.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="recovery-confirm-password">Potwierdź hasło</Label>
              <div className="relative">
                <Input
                  id="recovery-confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Potwierdź nowe hasło"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>
              {confirmPassword && !passwordsMatch && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <XCircle className="w-3 h-3" />
                  Hasła nie są identyczne
                </p>
              )}
              {confirmPassword && passwordsMatch && (
                <p className="text-xs text-green-500 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Hasła są identyczne
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={handleCloseRecoveryDialog}
              disabled={changingPassword}
            >
              Anuluj
            </Button>
            <Button
              onClick={handleRecoveryPasswordChange}
              disabled={!canSavePassword || changingPassword}
              className="bg-gradient-to-r from-blue-500 to-indigo-600"
            >
              {changingPassword && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Zapisz nowe hasło
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Top Header - Uses theme colors from user preference */}
      {useBottomNav && (
        <header className={cn("lg:hidden fixed top-0 left-0 right-0 z-50 shadow-lg", roleColors.headerBg)}>
          <div className="relative flex items-center justify-center h-14">
            {/* User Avatar + Role switcher - Left */}
            <div className="absolute left-4 flex items-center gap-2">
              <Link to={createPageUrl('Profile')}>
                <Avatar className="h-9 w-9 ring-2 ring-white/50 shadow-md cursor-pointer hover:ring-white transition-all">
                  <AvatarImage src={user?.avatar_url} />
                  <AvatarFallback className="bg-white/20 text-white font-semibold text-sm">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
              </Link>
              <RoleSwitcherMobileButton />
            </div>
            {/* Centered Logo and App Name */}
            <div className="flex items-center gap-2">
              <img src={logo} alt="KConnect Logo" className="w-8 h-8 rounded-full shadow-md bg-white p-0.5" />
              <span className="font-bold text-white">KConnect</span>
            </div>
            {/* Notification Bell - Positioned Right */}
            <Link to={createPageUrl('Notifications')} className="absolute right-4">
              <Button variant="ghost" size="icon" className="relative text-white hover:bg-white/20 h-11 w-11 min-h-[44px] min-w-[44px]">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 border-2 border-white">
                    {unreadCount}
                  </Badge>
                )}
              </Button>
            </Link>
          </div>
        </header>
      )}

      {/* Mobile Header - Hidden for users with bottom nav */}
      {!useBottomNav && (
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <div className="flex items-center justify-between px-4 h-16">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className={cn("w-72 p-0", roleColors.sidebarBg)}>
              <div className="flex flex-col h-full">
                <div className="p-6 border-b border-white/10">
                  <div className="flex items-center gap-2">
                    <img src={logo} alt="KConnect Logo" className="w-8 h-8 rounded-full shadow-lg bg-white p-0.5" />
                    <span className="font-bold text-base text-white">KConnect</span>
                  </div>
                </div>
                <RoleSwitcherMobile />
                <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                  {navItems.map((item) => {
                    // Handle grouped items (like Struktura)
                    if (item.isGroup) {
                      const isAnyChildActive = item.children?.some(child => currentPageName === child.page);
                      return (
                        <div key={item.name} className="space-y-1">
                          <button
                            onClick={() => setStrukturaExpanded(!strukturaExpanded)}
                            className={cn(
                              "flex items-center gap-3 px-4 py-3 rounded-xl transition-all w-full",
                              isAnyChildActive ? "bg-white/10 text-white" : roleColors.navItem
                            )}
                          >
                            <item.icon className={cn("w-5 h-5", isAnyChildActive ? roleColors.navActiveIcon : "")} />
                            <span className="font-medium">{item.name}</span>
                            <ChevronDown className={cn(
                              "w-4 h-4 ml-auto text-white/70 transition-transform",
                              strukturaExpanded ? "" : "-rotate-90"
                            )} />
                          </button>
                          {/* Animated collapsible children */}
                          <div className={cn(
                            "overflow-hidden transition-all duration-300 ease-out",
                            strukturaExpanded
                              ? "max-h-96 opacity-100"
                              : "max-h-0 opacity-0"
                          )}>
                            <div className="ml-4 pl-4 border-l border-white/20 space-y-1 pt-1">
                              {item.children.map((child) => (
                                <Link
                                  key={child.page}
                                  to={createPageUrl(child.page)}
                                  onClick={() => setMobileMenuOpen(false)}
                                  className={cn(
                                    "flex items-center gap-3 px-3 py-2 rounded-lg transition-all",
                                    currentPageName === child.page
                                      ? "bg-white/20 text-white font-semibold"
                                      : "text-white/60 hover:text-white hover:bg-white/10"
                                  )}
                                >
                                  <child.icon className={cn("w-4 h-4", currentPageName === child.page ? "text-white" : "")} />
                                  <span className="text-sm">{child.name}</span>
                                </Link>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <Link
                        key={item.page}
                        to={createPageUrl(item.page)}
                        onClick={() => setMobileMenuOpen(false)}
                        className={cn(
                          "flex items-center gap-3 px-4 py-3 rounded-xl transition-all",
                          currentPageName === item.page
                            ? roleColors.navActive
                            : roleColors.navItem
                        )}
                      >
                        <item.icon className={cn("w-5 h-5", currentPageName === item.page ? roleColors.navActiveIcon : "")} />
                        <span className="font-medium">{item.name}</span>
                      </Link>
                    );
                  })}
                </nav>
                <div className="p-4 space-y-1 border-t border-white/10">
                  {(user?.active_role || user?.roles?.[0]) !== 'super_admin' && (
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-white/80 hover:text-white hover:bg-white/15"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        handleReportBug();
                      }}
                    >
                      <Bug className="w-5 h-5 mr-3" />
                      Zgłoś problem
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-white/80 hover:text-white hover:bg-white/15"
                    onClick={handleLogout}
                  >
                    <LogOut className="w-5 h-5 mr-3" />
                    {t('logout')}
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-2">
            <img src={logo} alt="KConnect Logo" className="w-6 h-6 rounded-full shadow-md shadow-blue-500/25" />
            <span className="font-bold text-sm text-slate-900">KConnect</span>
          </div>

          <div className="flex items-center gap-2">
            <Link to={createPageUrl('Notifications')}>
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5 text-slate-600" />
                {unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500">
                    {unreadCount}
                  </Badge>
                )}
              </Button>
            </Link>
          </div>
        </div>
      </header>
      )}

      {/* Desktop Sidebar */}
      <aside className={cn("hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:w-64", roleColors.sidebarBg)}>
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src={logo} alt="KConnect Logo" className="w-10 h-10 rounded-full shadow-lg bg-white p-0.5 object-cover" />
              <span className="font-bold text-base text-white">KConnect</span>
            </div>
            {/* Desktop Notification Bell */}
            <Link to={createPageUrl('Notifications')}>
              <Button variant="ghost" size="icon" className="relative text-white hover:bg-white/20 h-9 w-9">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 border-2 border-white text-xs">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
              </Button>
            </Link>
          </div>
        </div>

        {user && (
          <div className="mx-4 mt-4 space-y-3">
            {/* Tenant Info */}
            {user.tenant_name && (
              <div className={cn("p-3 rounded-xl border backdrop-blur-sm", roleColors.infoCardBg)}>
                <div className="flex items-center gap-2">
                  <Building2 className={cn("w-4 h-4", roleColors.infoCardIcon)} />
                  <div className="flex-1 min-w-0">
                    <p className={cn("text-xs font-medium", roleColors.infoCardLabel)}>{t('organization')}</p>
                    <p className={cn("text-sm font-semibold truncate", roleColors.infoCardText)}>
                      {user.tenant_name}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Branch Info for Parent */}
            {((user.active_role || user.roles?.[0]) === 'parent') && user.child_branches && user.child_branches.length > 0 && (
              <div className={cn("p-3 rounded-xl border backdrop-blur-sm", roleColors.infoCardBg)}>
                <div className="flex items-center gap-2">
                  <Building2 className={cn("w-4 h-4", roleColors.infoCardIcon)} />
                  <div className="flex-1 min-w-0">
                    <p className={cn("text-xs font-medium", roleColors.infoCardLabel)}>{t('branch')}</p>
                    <p className={cn("text-sm font-semibold truncate", roleColors.infoCardText)}>
                      {user.child_branches.join(', ')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Branch Info for Teacher */}
            {(user.active_role || user.roles?.[0]) === 'teacher' && user.branch_name && (
              <div className={cn("p-3 rounded-xl border backdrop-blur-sm", roleColors.infoCardBg)}>
                <div className="flex items-center gap-2">
                  <Building2 className={cn("w-4 h-4", roleColors.infoCardIcon)} />
                  <div className="flex-1 min-w-0">
                    <p className={cn("text-xs font-medium", roleColors.infoCardLabel)}>{t('branch')}</p>
                    <p className={cn("text-sm font-semibold truncate", roleColors.infoCardText)}>
                      {user.branch_name}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Branch Info for Branch Admin - Moved to Home page */}

            {/* Role / context switcher - only visible when user has multiple roles (e.g. teacher + parent at same branch) */}
            {hasMultipleContexts && (
              <div className="mt-2">
                <RoleSwitcher />
              </div>
            )}

            {/* User Profile */}
            <Link to={createPageUrl('Profile')} className="block">
              <div className={cn("p-4 rounded-xl border backdrop-blur-sm hover:bg-white/20 transition-all cursor-pointer", roleColors.infoCardBg)}>
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10 ring-2 ring-white/50 shadow-md">
                    <AvatarImage src={user.avatar_url} />
                    <AvatarFallback className="bg-white/30 text-white font-semibold">
                      {user.first_name?.charAt(0) || user.email?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className={cn("text-sm font-semibold truncate", roleColors.infoCardText)}>
                      {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email}
                    </p>
                    <p className={cn("text-xs", roleColors.infoCardLabel)}>
                      {ROLE_TRANSLATIONS[user.active_role || user.roles?.[0] || 'parent'] || 'Użytkownik'}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/60" />
                </div>
              </div>
            </Link>

            {/* Context info */}
            <div className="px-2 pt-2 mt-1 border-t border-white/10 text-xs text-white/60 space-y-1">
              {user.tenant_name && (
                <p>Tenant: <span className="font-semibold text-white/80">{user.tenant_name}</span></p>
              )}
              {branchNameToDisplay && (currentRoleForDisplay !== 'branch_admin') && (
                <p>Branch: <span className="font-semibold text-white/80">{branchNameToDisplay}</span></p>
              )}
            </div>
          </div>
        )}

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            // Handle grouped items (like Struktura)
            if (item.isGroup) {
              const isAnyChildActive = item.children?.some(child => currentPageName === child.page);
              return (
                <div key={item.name} className="space-y-1">
                  {/* Group header - clickable to expand/collapse */}
                  <button
                    onClick={() => setStrukturaExpanded(!strukturaExpanded)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 rounded-xl transition-all w-full group",
                      isAnyChildActive
                        ? "bg-white/10 text-white"
                        : roleColors.navItem
                    )}
                  >
                    <item.icon className={cn(
                      "w-5 h-5 transition-transform group-hover:scale-110",
                      isAnyChildActive ? roleColors.navActiveIcon : ""
                    )} />
                    <span className="font-medium">{item.name}</span>
                    <ChevronDown className={cn(
                      "w-4 h-4 ml-auto text-white/70 transition-transform",
                      strukturaExpanded ? "" : "-rotate-90"
                    )} />
                  </button>

                  {/* Animated collapsible children */}
                  <div className={cn(
                    "overflow-hidden transition-all duration-300 ease-out",
                    strukturaExpanded
                      ? "max-h-96 opacity-100"
                      : "max-h-0 opacity-0"
                  )}>
                    <div className="ml-4 pl-4 border-l border-white/20 space-y-1 pt-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.page}
                          to={createPageUrl(child.page)}
                          className={cn(
                            "flex items-center gap-3 px-3 py-2 rounded-lg transition-all group",
                            currentPageName === child.page
                              ? "bg-white/20 text-white font-semibold"
                              : "text-white/60 hover:text-white hover:bg-white/10"
                          )}
                        >
                          <child.icon className={cn(
                            "w-4 h-4 transition-transform group-hover:scale-110",
                            currentPageName === child.page ? "text-white" : ""
                          )} />
                          <span className="text-sm">{child.name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            // Regular nav item
            return (
              <Link
                key={item.page}
                to={createPageUrl(item.page)}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-all group",
                  currentPageName === item.page
                    ? roleColors.navActive
                    : roleColors.navItem
                )}
              >
                <item.icon className={cn(
                  "w-5 h-5 transition-transform group-hover:scale-110",
                  currentPageName === item.page ? roleColors.navActiveIcon : ""
                )} />
                <span className="font-medium">{item.name}</span>
                {currentPageName === item.page && (
                  <ChevronRight className="w-4 h-4 ml-auto text-white" />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 space-y-1 border-t border-white/10">
          {(user?.active_role || user?.roles?.[0]) !== 'super_admin' && (
            <Button
              variant="ghost"
              className="w-full justify-start text-white/80 hover:text-white hover:bg-white/15"
              onClick={handleReportBug}
            >
              <Bug className="w-5 h-5 mr-3" />
              Zgłoś problem
            </Button>
          )}
          <Button
            variant="ghost"
            className="w-full justify-start text-white/80 hover:text-white hover:bg-white/15"
            onClick={handleLogout}
          >
            <LogOut className="w-5 h-5 mr-3" />
            {t('logout')}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={cn(
        "bg-white/80",
        useBottomNav
          ? "fixed top-14 left-0 right-0 overflow-y-auto bottom-nav-safe" // Fixed positioning with header and bottom nav + safe area
          : "min-h-screen lg:pl-64 pt-16 lg:pt-0" // Normal layout with sidebar
      )}>
        <div className="p-4 md:p-6 lg:p-8">
          <PageTransition>
            {children}
          </PageTransition>
        </div>
      </main>

      {/* Tutorial Overlay */}
      <TutorialOverlay />

      {/* Bottom Navigation for Parents on Mobile */}
      {isParentOnMobile && (
        <BottomNav onLogout={handleLogout} onReportBug={handleReportBug} />
      )}

      {/* Bottom Navigation for Teachers on Mobile */}
      {isTeacherOnMobile && (
        <TeacherBottomNav onLogout={handleLogout} onReportBug={handleReportBug} />
      )}

      {/* Bottom Navigation for Branch Admins on Mobile */}
      {isBranchAdminOnMobile && (
        <BranchAdminBottomNav onLogout={handleLogout} onReportBug={handleReportBug} />
      )}

      {/* Bottom Navigation for Super Admins on Mobile */}
      {isSuperAdminOnMobile && (
        <SuperAdminBottomNav onLogout={handleLogout} />
      )}
    </div>
  );
}

export default function Layout({ children, currentPageName }) {
  return (
    <LanguageProvider>
      <TutorialProvider>
        <LayoutContent currentPageName={currentPageName}>
          {children}
        </LayoutContent>
      </TutorialProvider>
    </LanguageProvider>
  );
}